/**
 * CalendarSyncManager Component
 * 
 * Root-level integration point for calendar auto-sync.
 * Mount this component in AppShell or app root to enable background sync.
 * 
 * Features:
 * - Auto-sync is OFF by default
 * - Uses fresh events from canonical store (Supabase)
 * - Never blocks Calendar page rendering
 * - Automatically stops on logout/unmount
 * 
 * @module CalendarSyncManager
 * @version 1.0.0
 */

import { useEffect } from 'react';
import { useCalendarAutoSync } from '../hooks/useCalendarAutoSync';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CalendarSyncManagerProps {
  /** Sync interval in milliseconds (default: 5 minutes) */
  intervalMs?: number;
  /** Whether to auto-enable sync on mount (default: false - OFF by default) */
  autoEnable?: boolean;
  /** Callback when sync state changes */
  onSyncStateChange?: (enabled: boolean) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Root integration component for calendar auto-sync.
 * 
 * This is a headless component - it renders nothing but manages sync lifecycle.
 * 
 * @example
 * // In AppShell.tsx
 * <CalendarSyncManager autoEnable={false} intervalMs={5 * 60 * 1000} />
 * 
 * // With user settings
 * <CalendarSyncManager 
 *   autoEnable={userSettings.calendarAutoSync} 
 *   onSyncStateChange={(enabled) => saveUserSettings({ calendarAutoSync: enabled })}
 * />
 */
export function CalendarSyncManager({
  intervalMs,
  autoEnable = false,
  onSyncStateChange,
}: CalendarSyncManagerProps): null {
  const { isEnabled } = useCalendarAutoSync({
    intervalMs,
    autoEnable,
  });
  
  // Notify parent of state changes
  useEffect(() => {
    onSyncStateChange?.(isEnabled);
  }, [isEnabled, onSyncStateChange]);
  
  // Headless component - renders nothing
  return null;
}

export default CalendarSyncManager;
